import React, { useState, useRef, useEffect } from 'react';
import axios from 'axios';
import Header from '@/components/Header';
import Footer from '@/components/Footer';
import { Lock, Save, Image as ImageIcon, Tag, Type, Loader2, CheckCircle, AlertCircle, Upload, Link as LinkIcon, Edit, Trash2, Plus, X as CloseIcon } from 'lucide-react';

const BlogAdmin = () => {
    // Force scroll enabled
    useEffect(() => {
        document.body.style.overflow = 'unset';
        window.scrollTo(0, 0);
    }, []);

    const [password, setPassword] = useState('');
    const [isAuthenticated, setIsAuthenticated] = useState(false);
    const [loading, setLoading] = useState(false);
    const [uploading, setUploading] = useState(false);
    const [status, setStatus] = useState(null); // 'success', 'error'
    const [message, setMessage] = useState('');
    const [posts, setPosts] = useState([]);
    const [isEditing, setIsEditing] = useState(false);
    const [editingId, setEditingId] = useState(null);
    const fileInputRef = useRef(null);

    const [postData, setPostData] = useState({
        title: '',
        description: '',
        category: '',
        image: '',
        tags: '',
        content: ''
    });

    const fetchPosts = async () => {
        try {
            const response = await axios.get('http://localhost:8000/api/blog');
            setPosts(response.data);
        } catch (error) {
            console.error("Error fetching posts:", error);
        }
    };

    useEffect(() => {
        if (isAuthenticated) {
            fetchPosts();
        }
    }, [isAuthenticated]);

    const handleLogin = (e) => {
        e.preventDefault();
        if (password === 'evolvance2025') {
            setIsAuthenticated(true);
            setStatus(null);
        } else {
            setStatus('error');
            setMessage('Contraseña incorrecta.');
        }
    };

    const handleFileUpload = async (e) => {
        const file = e.target.files[0];
        if (!file) return;

        setUploading(true);
        const formData = new FormData();
        formData.append('file', file);

        try {
            const response = await axios.post('http://localhost:8000/api/upload', formData, {
                headers: {
                    'Content-Type': 'multipart/form-data'
                }
            });
            setPostData({ ...postData, image: response.data.url });
        } catch (error) {
            console.error("Error al subir imagen:", error);
            alert("Error al subir la imagen local.");
        } finally {
            setUploading(false);
        }
    };

    const handleSubmit = async (e) => {
        e.preventDefault();
        setLoading(true);
        setStatus(null);

        try {
            const payload = {
                ...postData,
                tags: typeof postData.tags === 'string'
                    ? postData.tags.split(',').map(tag => tag.trim()).filter(tag => tag !== '')
                    : postData.tags,
                password: password
            };

            if (isEditing) {
                await axios.put(`http://localhost:8000/api/blog/${editingId}`, payload);
                setStatus('success');
                setMessage('¡Artículo actualizado con éxito!');
            } else {
                await axios.post('http://localhost:8000/api/blog', payload);
                setStatus('success');
                setMessage('¡Artículo publicado con éxito!');
            }

            // Reset and refresh
            resetForm();
            fetchPosts();
        } catch (error) {
            setStatus('error');
            setMessage(error.response?.data?.detail || 'Error al procesar el artículo.');
        } finally {
            setLoading(false);
        }
    };

    const resetForm = () => {
        setPostData({
            title: '',
            description: '',
            category: '',
            image: '',
            tags: '',
            content: ''
        });
        setIsEditing(false);
        setEditingId(null);
    };

    const handleEdit = (post) => {
        setPostData({
            title: post.title,
            description: post.description,
            category: post.category,
            image: post.image,
            tags: post.tags.join(', '),
            content: post.content
        });
        setIsEditing(true);
        setEditingId(post.id);
        window.scrollTo({ top: 0, behavior: 'smooth' });
    };

    const handleDelete = async (postId) => {
        if (!window.confirm('¿Estás seguro de que quieres eliminar este artículo?')) return;

        try {
            await axios.delete(`http://localhost:8000/api/blog/${postId}?password=${password}`);
            fetchPosts();
            if (editingId === postId) resetForm();
        } catch (error) {
            alert('Error al eliminar el artículo.');
        }
    };

    if (!isAuthenticated) {
        return (
            <div className="min-h-screen bg-[#1A1B1B] text-white flex flex-col">
                <Header />
                <div className="flex-grow flex items-center justify-center p-6 mt-20">
                    <div className="max-w-md w-full bg-[#202121] border border-[#D4AF37]/20 p-8 rounded-3xl shadow-2xl">
                        <div className="text-center mb-8">
                            <div className="w-16 h-16 bg-[#D4AF37]/10 rounded-full flex items-center justify-center mx-auto mb-4">
                                <Lock className="text-[#D4AF37] w-8 h-8" />
                            </div>
                            <h1 className="text-2xl font-outfit font-bold">Admin Privado - Blog</h1>
                            <p className="text-[#888] text-sm mt-2">Introduce la contraseña para gestionar contenido.</p>
                        </div>

                        <form onSubmit={handleLogin} className="space-y-4">
                            <div>
                                <input
                                    type="password"
                                    placeholder="Contraseña"
                                    value={password}
                                    onChange={(e) => setPassword(e.target.value)}
                                    className="w-full bg-[#1A1B1B] border border-[#D4AF37]/30 rounded-xl px-4 py-3 focus:outline-none focus:border-[#D4AF37] transition-all"
                                    required
                                />
                            </div>
                            {status === 'error' && (
                                <p className="text-red-400 text-sm flex items-center gap-2">
                                    <AlertCircle size={14} /> {message}
                                </p>
                            )}
                            <button
                                type="submit"
                                className="w-full bg-[#D4AF37] text-black font-bold py-3 rounded-xl hover:bg-[#C49F27] transition-all"
                            >
                                Acceder
                            </button>
                        </form>
                    </div>
                </div>
                <Footer />
            </div>
        );
    }

    return (
        <div className="min-h-screen bg-[#1A1B1B] text-white flex flex-col">
            <Header />
            <div className="h-24 md:h-32" aria-hidden="true"></div>
            <div className="flex-grow container-custom pt-10 pb-20 relative z-10">
                <div className="max-w-5xl mx-auto mt-8">
                    <div className="flex flex-col md:flex-row md:items-center justify-between gap-6 mb-12">
                        <div>
                            <h1 className="text-3xl font-outfit font-bold mb-2">
                                {isEditing ? 'Editando Artículo' : 'Editor de Artículos'}
                            </h1>
                            <p className="text-[#888]">
                                {isEditing ? 'Modifica el contenido de tu publicación.' : 'Crea y publica nuevo contenido para el blog.'}
                            </p>
                        </div>
                        <div className="flex gap-4">
                            {isEditing && (
                                <button
                                    onClick={resetForm}
                                    className="text-white border border-white/20 px-4 py-2 rounded-xl text-sm hover:bg-white/10 transition-all flex items-center gap-2"
                                >
                                    <Plus size={16} /> Nuevo Post
                                </button>
                            )}
                            <button
                                onClick={() => setIsAuthenticated(false)}
                                className="text-[#D4AF37] border border-[#D4AF37]/30 px-4 py-2 rounded-xl text-sm hover:bg-[#D4AF37]/10 transition-all font-semibold"
                            >
                                Cerrar Sesión
                            </button>
                        </div>
                    </div>

                    <div className="grid lg:grid-cols-3 gap-12">
                        {/* Form Section */}
                        <div className="lg:col-span-2 space-y-8">
                            <form onSubmit={handleSubmit} className="space-y-8 bg-[#202121] p-8 rounded-3xl border border-white/5 shadow-xl">
                                <div className="grid md:grid-cols-2 gap-8">
                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-[#D4AF37] flex items-center gap-2">
                                                <Type size={16} /> Título del Artículo
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Ej: Tendencias de IA 2026"
                                                value={postData.title}
                                                onChange={(e) => setPostData({ ...postData, title: e.target.value })}
                                                className="w-full bg-[#1A1B1B] border border-[#D4AF37]/20 rounded-xl px-4 py-3 focus:outline-none focus:border-[#D4AF37] transition-all"
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-[#D4AF37] flex items-center gap-2">
                                                <Tag size={16} /> Categoría Principal
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="Ej: Tecnología"
                                                value={postData.category}
                                                onChange={(e) => setPostData({ ...postData, category: e.target.value })}
                                                className="w-full bg-[#1A1B1B] border border-[#D4AF37]/20 rounded-xl px-4 py-3 focus:outline-none focus:border-[#D4AF37] transition-all"
                                                required
                                            />
                                        </div>

                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-[#D4AF37] flex items-center gap-2">
                                                <Tag size={16} /> Etiquetas
                                            </label>
                                            <input
                                                type="text"
                                                placeholder="IA, Automatización"
                                                value={postData.tags}
                                                onChange={(e) => setPostData({ ...postData, tags: e.target.value })}
                                                className="w-full bg-[#1A1B1B] border border-[#D4AF37]/20 rounded-xl px-4 py-3 focus:outline-none focus:border-[#D4AF37] transition-all"
                                            />
                                        </div>
                                    </div>

                                    <div className="space-y-6">
                                        <div className="space-y-2">
                                            <label className="text-sm font-semibold text-[#D4AF37] flex items-center gap-2">
                                                <ImageIcon size={16} /> Imagen del Artículo
                                            </label>

                                            <div className="flex gap-2">
                                                <button
                                                    type="button"
                                                    onClick={() => fileInputRef.current.click()}
                                                    className="flex-1 bg-[#D4AF37]/10 border border-[#D4AF37]/30 text-[#D4AF37] py-3 rounded-xl hover:bg-[#D4AF37]/20 transition-all flex items-center justify-center gap-2 text-sm font-semibold"
                                                >
                                                    {uploading ? <Loader2 className="animate-spin w-4 h-4" /> : <Upload size={18} />}
                                                    Subir Local
                                                </button>
                                                <input
                                                    type="file"
                                                    ref={fileInputRef}
                                                    onChange={handleFileUpload}
                                                    className="hidden"
                                                    accept="image/*"
                                                />
                                            </div>

                                            <div className="relative">
                                                <div className="absolute left-4 top-1/2 -translate-y-1/2 text-[#D4AF37]/50">
                                                    <LinkIcon size={14} />
                                                </div>
                                                <input
                                                    type="url"
                                                    placeholder="URL externa..."
                                                    value={postData.image}
                                                    onChange={(e) => setPostData({ ...postData, image: e.target.value })}
                                                    className="w-full bg-[#1A1B1B] border border-[#D4AF37]/20 rounded-xl pl-10 pr-4 py-3 focus:outline-none focus:border-[#D4AF37] transition-all text-sm"
                                                />
                                            </div>
                                        </div>
                                        <div className="aspect-video bg-[#1A1B1B] border border-dashed border-[#D4AF37]/30 rounded-2xl flex items-center justify-center overflow-hidden relative group">
                                            {postData.image ? (
                                                <img src={postData.image} alt="Preview" className="w-full h-full object-cover" />
                                            ) : (
                                                <p className="text-[#888] text-sm italic">Previsualización</p>
                                            )}
                                        </div>
                                    </div>
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-[#D4AF37] flex items-center gap-2">
                                        <Type size={16} /> Descripción Corta
                                    </label>
                                    <textarea
                                        placeholder="Resumen atractivo..."
                                        value={postData.description}
                                        onChange={(e) => setPostData({ ...postData, description: e.target.value })}
                                        className="w-full bg-[#1A1B1B] border border-[#D4AF37]/20 rounded-xl px-4 py-3 min-h-[80px] focus:outline-none focus:border-[#D4AF37] transition-all"
                                        required
                                    />
                                </div>

                                <div className="space-y-2">
                                    <label className="text-sm font-semibold text-[#D4AF37] flex items-center gap-2">
                                        <Type size={16} /> Contenido Completo
                                    </label>
                                    <textarea
                                        placeholder="Cuerpo del artículo..."
                                        value={postData.content}
                                        onChange={(e) => setPostData({ ...postData, content: e.target.value })}
                                        className="w-full bg-[#1A1B1B] border border-[#D4AF37]/20 rounded-xl px-4 py-3 min-h-[250px] focus:outline-none focus:border-[#D4AF37] transition-all"
                                    />
                                </div>

                                {status && (
                                    <div className={`${status === 'success' ? 'bg-green-500/10 border-green-500/50 text-green-400' : 'bg-red-500/10 border-red-500/50 text-red-400'} p-4 rounded-xl flex items-center gap-3 animate-fade-in`}>
                                        {status === 'success' ? <CheckCircle size={20} /> : <AlertCircle size={20} />} {message}
                                    </div>
                                )}

                                <div className="flex justify-end gap-4">
                                    {isEditing && (
                                        <button
                                            type="button"
                                            onClick={resetForm}
                                            className="px-6 py-4 rounded-2xl border border-white/10 hover:bg-white/5 transition-all font-bold"
                                        >
                                            Cancelar
                                        </button>
                                    )}
                                    <button
                                        type="submit"
                                        disabled={loading}
                                        className="bg-[#D4AF37] text-black font-bold px-8 py-4 rounded-2xl hover:scale-105 active:scale-95 transition-all flex items-center gap-2 disabled:opacity-50 shadow-lg"
                                    >
                                        {loading ? <Loader2 className="animate-spin w-5 h-5" /> : <Save size={20} />}
                                        {isEditing ? 'Actualizar Artículo' : 'Publicar Artículo'}
                                    </button>
                                </div>
                            </form>
                        </div>

                        {/* List Section */}
                        <div className="space-y-6">
                            <h2 className="text-xl font-outfit font-bold flex items-center gap-2">
                                <ImageIcon className="text-[#D4AF37]" size={20} /> Artículos Publicados
                            </h2>
                            <div className="space-y-4 max-h-[800px] overflow-y-auto pr-2 custom-scrollbar">
                                {posts.length === 0 ? (
                                    <p className="text-[#555] italic text-sm">No hay artículos publicados aún.</p>
                                ) : (
                                    posts.map(post => (
                                        <div key={post.id} className="bg-[#202121] border border-white/5 p-4 rounded-2xl group hover:border-[#D4AF37]/30 transition-all">
                                            <div className="flex gap-4">
                                                <div className="w-16 h-16 rounded-xl overflow-hidden flex-shrink-0 bg-[#1A1B1B]">
                                                    <img src={post.image} alt="" className="w-full h-full object-cover" />
                                                </div>
                                                <div className="flex-grow min-w-0">
                                                    <h3 className="font-bold text-sm truncate group-hover:text-[#D4AF37] transition-colors">{post.title}</h3>
                                                    <p className="text-[#888] text-[10px] mb-2">{post.date} • {post.category}</p>
                                                    <div className="flex gap-2">
                                                        <button
                                                            onClick={() => handleEdit(post)}
                                                            className="p-1.5 rounded-lg bg-blue-500/10 text-blue-400 hover:bg-blue-500/20 transition-all"
                                                            title="Editar"
                                                        >
                                                            <Edit size={14} />
                                                        </button>
                                                        <button
                                                            onClick={() => handleDelete(post.id)}
                                                            className="p-1.5 rounded-lg bg-red-500/10 text-red-400 hover:bg-red-500/20 transition-all"
                                                            title="Eliminar"
                                                        >
                                                            <Trash2 size={14} />
                                                        </button>
                                                    </div>
                                                </div>
                                            </div>
                                        </div>
                                    ))
                                )}
                            </div>
                        </div>
                    </div>
                </div>
            </div>
            <Footer />
        </div>
    );
};

export default BlogAdmin;
